/**
 * Workspace SAML settings.
 *
 * Workspace admins need stable SP values before an IdP config exists. The UI is
 * intentionally setup-oriented: copy StreamLain SP values, import IdP metadata,
 * verify domains, test, then enable.
 */
import { Button, notify, useConfirmModal } from '@affine/component';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { markBackendUnavailable } from '@affine/core/utils/backend-availability';
import { copyTextToClipboard } from '@affine/core/utils/clipboard';
import { useI18n } from '@affine/i18n';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { useService } from '@toeverything/infra';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  SettingsActionRow,
  SettingsContentDivider,
  SettingsContentSection,
  SettingsContentSurface,
  SettingsContentText,
  SettingsDataList,
  SettingsDataRow,
  SettingsNotice,
  SettingsTabbedContent,
} from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityCard,
  SettingsEntityList,
} from '../shared/settings-entity';
import { SettingsEntityPage } from '../shared/settings-entity-page';
import { SettingsTabs } from '../shared/settings-navigation';
import { requestWorkspaceSetting } from '../shared/workspace-settings-api';
import * as styles from './styles.css';

interface SamlServiceProviderDetails {
  spEntityId: string;
  spMetadataUrl: string;
  spAcsUrl: string;
  spLoginUrl: string;
}

interface SamlConfigState {
  configured: boolean;
  enabled: boolean;
  idpEntityId?: string;
  idpSsoUrl?: string;
  spEntityId: string;
  spMetadataUrl: string;
  spAcsUrl: string;
  spLoginUrl: string;
  serviceProvider?: SamlServiceProviderDetails;
  attributeMapping?: Record<string, string>;
  certificateConfigured?: boolean;
  verifiedDomains?: SamlVerifiedDomain[];
}

interface SamlVerifiedDomain {
  id: string;
  domain: string;
  status: 'pending' | 'active';
  txtRecordName: string;
  txtRecordValue: string;
  verifiedAt: string | null;
}

interface SamlResolvedMetadataPreview {
  idpEntityId: string;
  idpSsoUrl: string;
  certificateFingerprint: string;
  certificateExpiresAt: string;
}

type SetupMode = 'metadata-url' | 'metadata-xml' | 'manual';
type SamlSettingsTab = 'setup' | 'domains' | 'advanced';
type TranslateOptions = Record<string, string | number>;

const BACKEND_UNAVAILABLE_STATUS = new Set([502, 503, 504]);

async function readErrorMessage(
  res: Response,
  backendUnavailableMessage: string
): Promise<string> {
  if (BACKEND_UNAVAILABLE_STATUS.has(res.status)) {
    const error = new Error(`HTTP ${res.status}`);
    markBackendUnavailable(error);
    return backendUnavailableMessage;
  }

  const errorBody = (await res.json().catch(() => null)) as {
    message?: string;
  } | null;
  return errorBody?.message ?? `HTTP ${res.status}`;
}

async function fetchSaml(
  workspaceId: string,
  path: string,
  init?: RequestInit
) {
  return requestWorkspaceSetting(workspaceId, path, init);
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

function getServiceProvider(config: SamlConfigState | null) {
  if (!config) return null;
  return {
    spEntityId: config.serviceProvider?.spEntityId ?? config.spEntityId,
    spMetadataUrl:
      config.serviceProvider?.spMetadataUrl ?? config.spMetadataUrl,
    spAcsUrl: config.serviceProvider?.spAcsUrl ?? config.spAcsUrl,
    spLoginUrl: config.serviceProvider?.spLoginUrl ?? config.spLoginUrl,
  };
}

export const SamlPanel = () => {
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const [config, setConfig] = useState<SamlConfigState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resolvingMetadata, setResolvingMetadata] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    error?: string;
  } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [metadataPreview, setMetadataPreview] =
    useState<SamlResolvedMetadataPreview | null>(null);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SamlSettingsTab>('setup');

  const [setupMode, setSetupMode] = useState<SetupMode>('metadata-url');
  const [idpMetadataUrl, setIdpMetadataUrl] = useState('');
  const [idpMetadataXml, setIdpMetadataXml] = useState('');
  const [idpEntityId, setIdpEntityId] = useState('');
  const [idpSsoUrl, setIdpSsoUrl] = useState('');
  const [idpCertificate, setIdpCertificate] = useState('');
  const [emailAttr, setEmailAttr] = useState('email');
  const [nameAttr, setNameAttr] = useState('name');
  const [enabled, setEnabled] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [domainBusy, setDomainBusy] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);

  const tr = useCallback(
    (key: string, fallback: string, options?: TranslateOptions) => {
      const fn = (t as unknown as Record<string, unknown>)[key];
      return typeof fn === 'function'
        ? (fn as (options?: TranslateOptions) => string)(options)
        : fallback;
    },
    [t]
  );
  const backendUnavailableMessage = tr(
    'com.streamlain.settings.workspace.saml.backend-unavailable',
    `${BRAND_DISPLAY_NAME} cannot be reached right now. Please try again later.`
  );

  const syncConfigToForm = useCallback((nextConfig: SamlConfigState) => {
    setConfig(nextConfig);
    setIdpEntityId(nextConfig.idpEntityId ?? '');
    setIdpSsoUrl(nextConfig.idpSsoUrl ?? '');
    setEmailAttr(nextConfig.attributeMapping?.email ?? 'email');
    setNameAttr(nextConfig.attributeMapping?.name ?? 'name');
    setEnabled(Boolean(nextConfig.enabled));
    if (nextConfig.configured) {
      setSetupMode('manual');
    }
  }, []);

  const reloadConfig = useCallback(async () => {
    const res = await fetchSaml(
      workspaceId,
      `/api/workspace/${workspaceId}/saml-config`
    );
    if (!res.ok) {
      throw new Error(await readErrorMessage(res, backendUnavailableMessage));
    }
    const nextConfig = (await res.json()) as SamlConfigState;
    syncConfigToForm(nextConfig);
    return nextConfig;
  }, [backendUnavailableMessage, syncConfigToForm, workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    let disposed = false;
    setLoading(true);
    void fetchSaml(workspaceId, `/api/workspace/${workspaceId}/saml-config`)
      .then(async res => {
        if (!res.ok) {
          throw new Error(
            await readErrorMessage(res, backendUnavailableMessage)
          );
        }
        return (await res.json()) as SamlConfigState;
      })
      .then(data => {
        if (!disposed) {
          syncConfigToForm(data);
        }
      })
      .catch(error => {
        if (!disposed) {
          setSaveError(getErrorMessage(error));
        }
      })
      .finally(() => {
        if (!disposed) {
          setLoading(false);
        }
      });
    return () => {
      disposed = true;
    };
  }, [backendUnavailableMessage, syncConfigToForm, workspaceId]);

  const serviceProvider = getServiceProvider(config);
  const verifiedDomains = config?.verifiedDomains ?? [];
  const hasActiveDomain = verifiedDomains.some(
    domain => domain.status === 'active'
  );
  const canEnable = Boolean(config?.configured && hasActiveDomain);
  const canSave = useMemo(() => {
    if (setupMode === 'metadata-url') {
      return Boolean(idpMetadataUrl.trim());
    }
    if (setupMode === 'metadata-xml') {
      return Boolean(idpMetadataXml.trim());
    }
    return Boolean(
      idpEntityId.trim() &&
      idpSsoUrl.trim() &&
      (idpCertificate.trim() || config?.certificateConfigured)
    );
  }, [
    config?.certificateConfigured,
    idpCertificate,
    idpEntityId,
    idpMetadataUrl,
    idpMetadataXml,
    idpSsoUrl,
    setupMode,
  ]);

  const copyValue = useCallback(
    async (value: string) => {
      const copied = await copyTextToClipboard(value);
      if (copied) {
        notify.success({
          title: tr(
            'com.streamlain.settings.workspace.saml.copy-success',
            'Copied'
          ),
        });
      } else {
        notify.error({
          title: tr(
            'com.streamlain.settings.workspace.saml.copy-failed',
            'Copy failed'
          ),
        });
      }
    },
    [tr]
  );

  const resolveMetadata = useCallback(async () => {
    if (!workspaceId) return;
    setResolvingMetadata(true);
    setMetadataError(null);
    setMetadataPreview(null);
    try {
      const body =
        setupMode === 'metadata-url'
          ? { metadataUrl: idpMetadataUrl.trim() }
          : { metadataXml: idpMetadataXml.trim() };
      const res = await fetchSaml(
        workspaceId,
        `/api/workspace/${workspaceId}/saml-config/idp-metadata/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        throw new Error(await readErrorMessage(res, backendUnavailableMessage));
      }
      const preview = (await res.json()) as SamlResolvedMetadataPreview;
      setMetadataPreview(preview);
      setIdpEntityId(preview.idpEntityId);
      setIdpSsoUrl(preview.idpSsoUrl);
    } catch (error) {
      setMetadataError(getErrorMessage(error));
    } finally {
      setResolvingMetadata(false);
    }
  }, [
    backendUnavailableMessage,
    idpMetadataUrl,
    idpMetadataXml,
    setupMode,
    workspaceId,
  ]);

  const handleSave = useCallback(async () => {
    if (!workspaceId) return;
    if (enabled && !hasActiveDomain) {
      setSaveError(
        tr(
          'com.streamlain.settings.workspace.saml.enable-needs-domain',
          'Verify at least one email domain before enabling SSO.'
        )
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    setTestResult(null);
    try {
      const body: Record<string, unknown> = {
        attributeMapping: { email: emailAttr, name: nameAttr },
        enabled,
      };
      if (setupMode === 'metadata-url') {
        body.idpMetadataUrl = idpMetadataUrl.trim();
      } else if (setupMode === 'metadata-xml') {
        body.idpMetadataXml = idpMetadataXml.trim();
      } else {
        body.idpEntityId = idpEntityId.trim();
        body.idpSsoUrl = idpSsoUrl.trim();
        if (idpCertificate.trim()) {
          body.idpCertificate = idpCertificate.trim();
        }
      }

      const saveRes = await fetchSaml(
        workspaceId,
        `/api/workspace/${workspaceId}/saml-config`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!saveRes.ok) {
        throw new Error(
          await readErrorMessage(saveRes, backendUnavailableMessage)
        );
      }
      await reloadConfig();
      setIdpCertificate('');
      notify.success({
        title: tr(
          'com.streamlain.settings.workspace.saml.save-success',
          'SSO configuration saved'
        ),
      });
    } catch (error) {
      setSaveError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }, [
    emailAttr,
    backendUnavailableMessage,
    enabled,
    hasActiveDomain,
    idpCertificate,
    idpEntityId,
    idpMetadataUrl,
    idpMetadataXml,
    idpSsoUrl,
    nameAttr,
    reloadConfig,
    setupMode,
    tr,
    workspaceId,
  ]);

  const handleTest = useCallback(async () => {
    if (!workspaceId) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetchSaml(
        workspaceId,
        `/api/workspace/${workspaceId}/saml-config/test`,
        {
          method: 'POST',
        }
      );
      if (!res.ok) {
        throw new Error(await readErrorMessage(res, backendUnavailableMessage));
      }
      setTestResult((await res.json()) as { ok: boolean; error?: string });
    } catch (error) {
      setTestResult({ ok: false, error: getErrorMessage(error) });
    } finally {
      setTesting(false);
    }
  }, [backendUnavailableMessage, workspaceId]);

  const openTestLogin = useCallback(() => {
    if (!serviceProvider?.spLoginUrl) return;
    window.open(serviceProvider.spLoginUrl, '_blank', 'noopener,noreferrer');
  }, [serviceProvider?.spLoginUrl]);

  const deleteConfig = useCallback(async () => {
    if (!workspaceId) return;
    setSaveError(null);
    try {
      const res = await fetchSaml(
        workspaceId,
        `/api/workspace/${workspaceId}/saml-config`,
        {
          method: 'DELETE',
        }
      );
      if (!res.ok) {
        throw new Error(await readErrorMessage(res, backendUnavailableMessage));
      }
      await reloadConfig();
      setIdpCertificate('');
      setEnabled(false);
    } catch (error) {
      setSaveError(getErrorMessage(error));
      await reloadConfig().catch(() => {});
    }
  }, [backendUnavailableMessage, reloadConfig, workspaceId]);

  const handleDelete = useCallback(async () => {
    if (!workspaceId) return;
    openConfirmModal({
      title: t['com.streamlain.settings.workspace.saml.delete'](),
      description: t['com.streamlain.settings.workspace.saml.delete-confirm'](),
      confirmText: t['com.streamlain.settings.workspace.saml.delete'](),
      cancelText: t['com.streamlain.confirmModal.button.cancel'](),
      confirmButtonOptions: { variant: 'error' },
      onConfirm: () => {
        void deleteConfig();
      },
    });
  }, [deleteConfig, openConfirmModal, t, workspaceId]);

  const handleAddDomain = useCallback(async () => {
    if (!workspaceId || !newDomain.trim()) return;
    setDomainBusy(true);
    setDomainError(null);
    try {
      const res = await fetchSaml(
        workspaceId,
        `/api/workspace/${workspaceId}/saml-config/domains`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domain: newDomain.trim() }),
        }
      );
      if (!res.ok) {
        throw new Error(await readErrorMessage(res, backendUnavailableMessage));
      }
      setNewDomain('');
      await reloadConfig();
    } catch (error) {
      setDomainError(getErrorMessage(error));
    } finally {
      setDomainBusy(false);
    }
  }, [backendUnavailableMessage, newDomain, reloadConfig, workspaceId]);

  const handleVerifyDomain = useCallback(
    async (domainId: string) => {
      if (!workspaceId) return;
      setDomainBusy(true);
      setDomainError(null);
      try {
        const res = await fetchSaml(
          workspaceId,
          `/api/workspace/${workspaceId}/saml-config/domains/${domainId}/verify`,
          {
            method: 'POST',
          }
        );
        if (!res.ok) {
          throw new Error(
            await readErrorMessage(res, backendUnavailableMessage)
          );
        }
        await reloadConfig();
      } catch (error) {
        setDomainError(getErrorMessage(error));
      } finally {
        setDomainBusy(false);
      }
    },
    [backendUnavailableMessage, reloadConfig, workspaceId]
  );

  const handleDeleteDomain = useCallback(
    async (domainId: string) => {
      if (!workspaceId) return;
      setDomainBusy(true);
      setDomainError(null);
      try {
        const res = await fetchSaml(
          workspaceId,
          `/api/workspace/${workspaceId}/saml-config/domains/${domainId}`,
          {
            method: 'DELETE',
          }
        );
        if (!res.ok) {
          throw new Error(
            await readErrorMessage(res, backendUnavailableMessage)
          );
        }
        await reloadConfig();
      } catch (error) {
        setDomainError(getErrorMessage(error));
      } finally {
        setDomainBusy(false);
      }
    },
    [backendUnavailableMessage, reloadConfig, workspaceId]
  );

  const handleDeleteDomainWithConfirm = useCallback(
    (domain: SamlVerifiedDomain) => {
      openConfirmModal({
        title: tr(
          'com.streamlain.settings.workspace.saml.domain-delete',
          'Delete domain'
        ),
        description: tr(
          'com.streamlain.settings.workspace.saml.domain-delete-confirm',
          'Remove this verified domain from the SSO configuration?',
          { domain: domain.domain }
        ),
        confirmText: tr(
          'com.streamlain.settings.workspace.saml.domain-delete',
          'Delete domain'
        ),
        cancelText: t['com.streamlain.confirmModal.button.cancel'](),
        confirmButtonOptions: { variant: 'error' },
        onConfirm: () => {
          void handleDeleteDomain(domain.id);
        },
      });
    },
    [handleDeleteDomain, openConfirmModal, t, tr]
  );

  const tabs = useMemo(
    () => [
      {
        id: 'setup',
        label: tr('com.streamlain.settings.workspace.saml.tab-setup', 'Setup'),
      },
      {
        id: 'domains',
        label: tr(
          'com.streamlain.settings.workspace.saml.tab-domains',
          'Domains'
        ),
      },
      {
        id: 'advanced',
        label: tr(
          'com.streamlain.settings.workspace.saml.tab-advanced',
          'Advanced'
        ),
      },
    ],
    [tr]
  );

  if (loading) {
    return (
      <SettingsEntityPage
        title={t['com.streamlain.settings.workspace.saml.title']()}
        subtitle={t['com.streamlain.settings.workspace.saml.description']()}
      >
        <SettingsContentSurface>
          <SettingsNotice
            title={tr(
              'com.streamlain.settings.workspace.saml.loading-title',
              'Loading SSO setup'
            )}
          >
            {tr(
              'com.streamlain.settings.workspace.saml.loading-body',
              'Workspace SSO configuration is being loaded.'
            )}
          </SettingsNotice>
        </SettingsContentSurface>
      </SettingsEntityPage>
    );
  }

  return (
    <SettingsEntityPage
      title={t['com.streamlain.settings.workspace.saml.title']()}
      subtitle={t['com.streamlain.settings.workspace.saml.description']()}
      controls={
        <SettingsTabs
          activeTab={activeTab}
          ariaLabel={tr(
            'com.streamlain.settings.workspace.saml.tabs-label',
            'SAML settings sections'
          )}
          onChange={tabId => setActiveTab(tabId as SamlSettingsTab)}
          tabs={tabs}
        />
      }
    >
      <SettingsTabbedContent>
        <SettingsContentSurface>
          <div className={styles.statusGrid}>
            <StatusTile
              label={tr(
                'com.streamlain.settings.workspace.saml.status-config',
                'Configuration'
              )}
              ready={Boolean(config?.configured)}
              value={
                config?.configured
                  ? t['com.streamlain.settings.workspace.saml.configured']()
                  : t['com.streamlain.settings.workspace.saml.not-configured']()
              }
            />
            <StatusTile
              label={tr(
                'com.streamlain.settings.workspace.saml.status-domain',
                'Domain'
              )}
              ready={hasActiveDomain}
              value={
                hasActiveDomain
                  ? tr(
                      'com.streamlain.settings.workspace.saml.domain-ready',
                      'Domain verified'
                    )
                  : tr(
                      'com.streamlain.settings.workspace.saml.domain-needed',
                      'Domain required'
                    )
              }
            />
            <StatusTile
              label={tr(
                'com.streamlain.settings.workspace.saml.status-sso',
                'SAML SSO'
              )}
              ready={Boolean(config?.enabled)}
              value={
                config?.enabled
                  ? tr(
                      'com.streamlain.settings.workspace.saml.enabled',
                      'Enabled'
                    )
                  : tr(
                      'com.streamlain.settings.workspace.saml.disabled',
                      'Disabled'
                    )
              }
            />
          </div>
        </SettingsContentSurface>

        {activeTab === 'setup' ? (
          <SettingsContentSurface>
            <SettingsContentSection
              title={tr(
                'com.streamlain.settings.workspace.saml.sp-title',
                `${BRAND_DISPLAY_NAME} service provider`
              )}
            >
              <SettingsContentText>
                {tr(
                  'com.streamlain.settings.workspace.saml.sp-description',
                  'Copy these values into your IdP application.'
                )}
              </SettingsContentText>
              <div className={styles.spValueList}>
                <CopyableDataRow
                  copyLabel={tr(
                    'com.streamlain.settings.workspace.saml.copy',
                    'Copy'
                  )}
                  label={tr(
                    'com.streamlain.settings.workspace.saml.sp-acs-url',
                    'ACS URL'
                  )}
                  onCopy={copyValue}
                  value={serviceProvider?.spAcsUrl}
                />
                <CopyableDataRow
                  copyLabel={tr(
                    'com.streamlain.settings.workspace.saml.copy',
                    'Copy'
                  )}
                  label={tr(
                    'com.streamlain.settings.workspace.saml.sp-entity-id',
                    'SP Entity ID / Audience URI'
                  )}
                  onCopy={copyValue}
                  value={serviceProvider?.spEntityId}
                />
                <CopyableDataRow
                  aside={
                    serviceProvider?.spMetadataUrl ? (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          window.open(
                            serviceProvider.spMetadataUrl,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                      >
                        {tr(
                          'com.streamlain.settings.workspace.saml.open-xml',
                          'Open XML'
                        )}
                      </Button>
                    ) : null
                  }
                  copyLabel={tr(
                    'com.streamlain.settings.workspace.saml.copy',
                    'Copy'
                  )}
                  label={t[
                    'com.streamlain.settings.workspace.saml.sp-metadata-url'
                  ]()}
                  onCopy={copyValue}
                  value={serviceProvider?.spMetadataUrl}
                />
                <CopyableDataRow
                  copyLabel={tr(
                    'com.streamlain.settings.workspace.saml.copy',
                    'Copy'
                  )}
                  label={tr(
                    'com.streamlain.settings.workspace.saml.sp-login-url',
                    'SP login URL'
                  )}
                  onCopy={copyValue}
                  value={serviceProvider?.spLoginUrl}
                />
              </div>
            </SettingsContentSection>

            <SettingsContentDivider />

            <SettingsContentSection
              title={tr(
                'com.streamlain.settings.workspace.saml.idp-title',
                'Identity provider'
              )}
            >
              <SettingsContentText>
                {tr(
                  'com.streamlain.settings.workspace.saml.idp-description',
                  'Use metadata when your IdP provides it. Manual setup stays available for restricted IdPs.'
                )}
              </SettingsContentText>
              <div className={styles.modeSelector}>
                <ModeButton
                  active={setupMode === 'metadata-url'}
                  onClick={() => setSetupMode('metadata-url')}
                >
                  {tr(
                    'com.streamlain.settings.workspace.saml.mode-url',
                    'Metadata URL'
                  )}
                </ModeButton>
                <ModeButton
                  active={setupMode === 'metadata-xml'}
                  onClick={() => setSetupMode('metadata-xml')}
                >
                  {tr(
                    'com.streamlain.settings.workspace.saml.mode-xml',
                    'Metadata XML'
                  )}
                </ModeButton>
                <ModeButton
                  active={setupMode === 'manual'}
                  onClick={() => setSetupMode('manual')}
                >
                  {tr(
                    'com.streamlain.settings.workspace.saml.mode-manual',
                    'Manual'
                  )}
                </ModeButton>
              </div>

              {setupMode === 'metadata-url' ? (
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {tr(
                      'com.streamlain.settings.workspace.saml.idp-metadata-url',
                      'IdP metadata URL'
                    )}
                  </span>
                  <input
                    className={styles.input}
                    value={idpMetadataUrl}
                    onChange={event => {
                      setIdpMetadataUrl(event.target.value);
                      setMetadataPreview(null);
                    }}
                    placeholder="https://idp.example.com/metadata"
                    type="url"
                  />
                </label>
              ) : null}

              {setupMode === 'metadata-xml' ? (
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {tr(
                      'com.streamlain.settings.workspace.saml.idp-metadata-xml',
                      'IdP metadata XML'
                    )}
                  </span>
                  <textarea
                    className={styles.textarea}
                    value={idpMetadataXml}
                    onChange={event => {
                      setIdpMetadataXml(event.target.value);
                      setMetadataPreview(null);
                    }}
                    placeholder="<EntityDescriptor ...>"
                    rows={8}
                  />
                </label>
              ) : null}

              {setupMode === 'manual' ? (
                <div className={styles.formGrid}>
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>
                      {t[
                        'com.streamlain.settings.workspace.saml.idp-entity-id'
                      ]()}
                    </span>
                    <input
                      className={styles.input}
                      value={idpEntityId}
                      onChange={event => setIdpEntityId(event.target.value)}
                    />
                  </label>
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>
                      {t[
                        'com.streamlain.settings.workspace.saml.idp-sso-url'
                      ]()}
                    </span>
                    <input
                      className={styles.input}
                      value={idpSsoUrl}
                      onChange={event => setIdpSsoUrl(event.target.value)}
                      type="url"
                    />
                  </label>
                  <label className={styles.fieldWide}>
                    <span className={styles.fieldLabel}>
                      {t[
                        'com.streamlain.settings.workspace.saml.idp-certificate'
                      ]()}
                    </span>
                    <textarea
                      className={styles.textarea}
                      value={idpCertificate}
                      onChange={event => setIdpCertificate(event.target.value)}
                      placeholder={
                        config?.certificateConfigured
                          ? t[
                              'com.streamlain.settings.workspace.saml.idp-certificate-placeholder-configured'
                            ]()
                          : '-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'
                      }
                      rows={5}
                    />
                  </label>
                </div>
              ) : (
                <SettingsActionRow>
                  <Button
                    variant="secondary"
                    disabled={
                      resolvingMetadata ||
                      (setupMode === 'metadata-url'
                        ? !idpMetadataUrl.trim()
                        : !idpMetadataXml.trim())
                    }
                    onClick={() => void resolveMetadata()}
                  >
                    {resolvingMetadata
                      ? tr(
                          'com.streamlain.settings.workspace.saml.metadata-checking',
                          'Checking metadata...'
                        )
                      : tr(
                          'com.streamlain.settings.workspace.saml.metadata-check',
                          'Check metadata'
                        )}
                  </Button>
                </SettingsActionRow>
              )}

              {metadataPreview ? (
                <SettingsNotice
                  title={tr(
                    'com.streamlain.settings.workspace.saml.metadata-valid',
                    'IdP metadata recognized'
                  )}
                >
                  <SettingsDataList>
                    <SettingsDataRow
                      label={t[
                        'com.streamlain.settings.workspace.saml.idp-entity-id'
                      ]()}
                      value={metadataPreview.idpEntityId}
                    />
                    <SettingsDataRow
                      label={t[
                        'com.streamlain.settings.workspace.saml.idp-sso-url'
                      ]()}
                      value={metadataPreview.idpSsoUrl}
                    />
                    <SettingsDataRow
                      label={tr(
                        'com.streamlain.settings.workspace.saml.cert-fingerprint',
                        'Certificate fingerprint'
                      )}
                      value={metadataPreview.certificateFingerprint}
                    />
                    <SettingsDataRow
                      label={tr(
                        'com.streamlain.settings.workspace.saml.cert-expires',
                        'Certificate expires'
                      )}
                      value={new Date(
                        metadataPreview.certificateExpiresAt
                      ).toLocaleDateString()}
                    />
                  </SettingsDataList>
                </SettingsNotice>
              ) : null}
              {metadataError ? (
                <div className={styles.errorMessage}>{metadataError}</div>
              ) : null}
            </SettingsContentSection>
          </SettingsContentSurface>
        ) : null}

        {activeTab === 'domains' ? (
          <SettingsContentSurface>
            <SettingsContentSection
              title={t[
                'com.streamlain.settings.workspace.saml.domains-title'
              ]()}
            >
              <SettingsContentText>
                {t[
                  'com.streamlain.settings.workspace.saml.domains-description'
                ]()}
              </SettingsContentText>
              <div className={styles.inlineForm}>
                <input
                  className={styles.input}
                  value={newDomain}
                  onChange={event => setNewDomain(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void handleAddDomain();
                    }
                  }}
                  placeholder={t[
                    'com.streamlain.settings.workspace.saml.domain-placeholder'
                  ]()}
                />
                <Button
                  disabled={domainBusy || !newDomain.trim()}
                  onClick={() => void handleAddDomain()}
                >
                  {t['com.streamlain.settings.workspace.saml.domain-add']()}
                </Button>
              </div>
              {domainError ? (
                <div className={styles.errorMessage}>{domainError}</div>
              ) : null}
              {verifiedDomains.length === 0 ? (
                <SettingsNotice
                  title={t[
                    'com.streamlain.settings.workspace.saml.domains-empty'
                  ]()}
                >
                  {tr(
                    'com.streamlain.settings.workspace.saml.domains-empty-body',
                    'Add your company email domain and verify the generated DNS TXT record.'
                  )}
                </SettingsNotice>
              ) : (
                <SettingsEntityList>
                  {verifiedDomains.map(domain => (
                    <SettingsEntityCard key={domain.id}>
                      <div className={styles.domainCardContent}>
                        <div className={styles.domainHeader}>
                          <strong>{domain.domain}</strong>
                          <SettingsBadge
                            variant={
                              domain.status === 'active' ? 'active' : 'soft'
                            }
                          >
                            {domain.status === 'active'
                              ? t[
                                  'com.streamlain.settings.workspace.saml.domain-status-active'
                                ]()
                              : t[
                                  'com.streamlain.settings.workspace.saml.domain-status-pending'
                                ]()}
                          </SettingsBadge>
                        </div>
                        <CopyableValue
                          copyLabel={tr(
                            'com.streamlain.settings.workspace.saml.copy',
                            'Copy'
                          )}
                          label={t[
                            'com.streamlain.settings.workspace.saml.domain-txt-name'
                          ]()}
                          onCopy={copyValue}
                          value={domain.txtRecordName}
                        />
                        <CopyableValue
                          copyLabel={tr(
                            'com.streamlain.settings.workspace.saml.copy',
                            'Copy'
                          )}
                          label={t[
                            'com.streamlain.settings.workspace.saml.domain-txt-value'
                          ]()}
                          onCopy={copyValue}
                          value={domain.txtRecordValue}
                        />
                        <SettingsActionRow>
                          {domain.status !== 'active' ? (
                            <Button
                              disabled={domainBusy}
                              variant="secondary"
                              onClick={() => void handleVerifyDomain(domain.id)}
                            >
                              {t[
                                'com.streamlain.settings.workspace.saml.domain-verify'
                              ]()}
                            </Button>
                          ) : null}
                          <Button
                            disabled={domainBusy}
                            variant="error"
                            onClick={() =>
                              handleDeleteDomainWithConfirm(domain)
                            }
                          >
                            {t[
                              'com.streamlain.settings.workspace.saml.domain-delete'
                            ]()}
                          </Button>
                        </SettingsActionRow>
                      </div>
                    </SettingsEntityCard>
                  ))}
                </SettingsEntityList>
              )}
            </SettingsContentSection>
          </SettingsContentSurface>
        ) : null}

        {activeTab === 'advanced' ? (
          <SettingsContentSurface>
            <SettingsContentSection
              title={t[
                'com.streamlain.settings.workspace.saml.attribute-mapping'
              ]()}
            >
              <div className={styles.formGrid}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {t[
                      'com.streamlain.settings.workspace.saml.email-attribute'
                    ]()}
                  </span>
                  <input
                    className={styles.input}
                    value={emailAttr}
                    onChange={event => setEmailAttr(event.target.value)}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {t[
                      'com.streamlain.settings.workspace.saml.name-attribute'
                    ]()}
                  </span>
                  <input
                    className={styles.input}
                    value={nameAttr}
                    onChange={event => setNameAttr(event.target.value)}
                  />
                </label>
              </div>
            </SettingsContentSection>

            <SettingsContentDivider />

            <SettingsContentSection
              title={tr(
                'com.streamlain.settings.workspace.saml.activation-title',
                'Activation'
              )}
            >
              {!canEnable ? (
                <SettingsNotice
                  title={tr(
                    'com.streamlain.settings.workspace.saml.activation-blocked-title',
                    'SSO cannot be enabled yet'
                  )}
                >
                  {tr(
                    'com.streamlain.settings.workspace.saml.activation-blocked-body',
                    'Save a valid IdP configuration and verify at least one email domain first.'
                  )}
                </SettingsNotice>
              ) : null}
              <label className={styles.toggleRow}>
                <input
                  checked={enabled}
                  disabled={!canEnable}
                  onChange={event => setEnabled(event.target.checked)}
                  type="checkbox"
                />
                <span>
                  {t['com.streamlain.settings.workspace.saml.enable']()}
                </span>
              </label>
            </SettingsContentSection>
          </SettingsContentSurface>
        ) : null}

        <SettingsContentSurface>
          <SettingsContentSection
            title={tr(
              'com.streamlain.settings.workspace.saml.actions-title',
              'Save and test'
            )}
          >
            <SettingsActionRow>
              <Button
                disabled={saving || !canSave}
                onClick={() => void handleSave()}
                variant="primary"
              >
                {saving
                  ? tr(
                      'com.streamlain.settings.workspace.saml.saving',
                      'Saving...'
                    )
                  : t['com.streamlain.settings.workspace.saml.save']()}
              </Button>
              <Button
                disabled={testing || !config?.configured}
                onClick={() => void handleTest()}
                variant="secondary"
              >
                {testing
                  ? tr(
                      'com.streamlain.settings.workspace.saml.testing',
                      'Testing...'
                    )
                  : t['com.streamlain.settings.workspace.saml.test']()}
              </Button>
              <Button
                disabled={!config?.enabled || !canEnable}
                onClick={openTestLogin}
                variant="secondary"
              >
                {tr(
                  'com.streamlain.settings.workspace.saml.test-login',
                  'Open SSO login'
                )}
              </Button>
              {config?.configured ? (
                <Button variant="error" onClick={() => void handleDelete()}>
                  {t['com.streamlain.settings.workspace.saml.delete']()}
                </Button>
              ) : null}
            </SettingsActionRow>
          </SettingsContentSection>
        </SettingsContentSurface>

        {saveError ? (
          <div className={styles.errorMessage}>{saveError}</div>
        ) : null}
        {testResult ? (
          <div
            className={
              testResult.ok ? styles.successMessage : styles.errorMessage
            }
          >
            {testResult.ok
              ? t['com.streamlain.settings.workspace.saml.test-success']()
              : t['com.streamlain.settings.workspace.saml.test-failed']({
                  error: testResult.error ?? 'Unknown error',
                })}
          </div>
        ) : null}
      </SettingsTabbedContent>
    </SettingsEntityPage>
  );
};

const StatusTile = ({
  label,
  ready,
  value,
}: {
  label: ReactNode;
  ready: boolean;
  value: ReactNode;
}) => (
  <div className={styles.statusTile}>
    <div className={styles.statusTileLabel}>{label}</div>
    <SettingsBadge variant={ready ? 'active' : 'soft'}>{value}</SettingsBadge>
  </div>
);

const ModeButton = ({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) => (
  <button
    className={active ? styles.modeButtonActive : styles.modeButton}
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

const CopyableDataRow = ({
  aside,
  copyLabel,
  label,
  onCopy,
  value,
}: {
  aside?: ReactNode;
  copyLabel: string;
  label: ReactNode;
  onCopy: (value: string) => Promise<void>;
  value?: string;
}) => (
  <div className={styles.spValueRow}>
    <div className={styles.spValueLabel}>{label}</div>
    <code className={styles.spValueCode} title={value}>
      {value ?? '-'}
    </code>
    <div className={styles.spValueActions}>
      {aside}
      {value ? (
        <Button variant="secondary" onClick={() => void onCopy(value)}>
          {copyLabel}
        </Button>
      ) : null}
    </div>
  </div>
);

const CopyableValue = ({
  copyLabel,
  label,
  onCopy,
  value,
}: {
  copyLabel: string;
  label: ReactNode;
  onCopy: (value: string) => Promise<void>;
  value: string;
}) => (
  <div className={styles.copyValue}>
    <div className={styles.copyValueLabel}>{label}</div>
    <code className={styles.inlineCode}>{value}</code>
    <div className={styles.copyValueActions}>
      <Button variant="secondary" onClick={() => void onCopy(value)}>
        {copyLabel}
      </Button>
    </div>
  </div>
);
